@if (strip) {
<section class="pkf" [attr.aria-labelledby]="headingId">
  <header class="pkf__head">
    <div class="pkf__title">
      <h3 [id]="headingId">{{ heading }}</h3>
      <p><span class="pkf__period"><i class="fal fa-calendar" aria-hidden="true"></i>{{ strip.periodLabel }}</span>@if (strip.accountType) {<span class="pkf__type">{{ strip.accountType }} accounts</span>}</p>
    </div>
    @if (canOpenReport) {
      <button type="button" class="pkf__report-link" aria-haspopup="dialog" (click)="open(strip.reportTab)">{{ reportLinkLabel }}<i class="fal fa-arrow-right" aria-hidden="true"></i></button>
    }
  </header>

  @if (strip.notice) {<p class="pkf__notice"><i class="fal fa-circle-info" aria-hidden="true"></i>{{ strip.notice }}</p>}

  @if (strip.figures.length) {
    <ul class="pkf__grid" role="list">
      @for (figure of strip.figures; track figure.id) {
        <li>
          @if (figure.reportTab && canOpenReport) {
            <button type="button" class="pkf__figure pkf__figure--link" [class.pkf__figure--missing]="figure.missing" [class.pkf__figure--flagged]="figure.flag" aria-haspopup="dialog" [title]="figure.exact" (click)="open(figure.reportTab)">
              <ng-container *ngTemplateOutlet="figureBody; context: { $implicit: figure }"></ng-container>
              <span class="pkf__cta">View in {{ tabLabel(figure.reportTab) }}<i class="fal fa-arrow-right" aria-hidden="true"></i><span class="pkf__sr"> (opens the financial report)</span></span>
            </button>
          } @else if (figure.action) {
            <button type="button" class="pkf__figure pkf__figure--link" [class.pkf__figure--missing]="figure.missing" [class.pkf__figure--flagged]="figure.flag" aria-haspopup="dialog" [title]="figure.exact" (click)="runAction(figure)">
              <ng-container *ngTemplateOutlet="figureBody; context: { $implicit: figure }"></ng-container>
              <span class="pkf__cta">{{ figure.action.label }}<i class="fal fa-arrow-right" aria-hidden="true"></i></span>
            </button>
          } @else {
            <div class="pkf__figure" [class.pkf__figure--missing]="figure.missing" [class.pkf__figure--flagged]="figure.flag" [title]="figure.exact">
              <ng-container *ngTemplateOutlet="figureBody; context: { $implicit: figure }"></ng-container>
            </div>
          }
        </li>
      }
    </ul>
  }

  @if (strip.check; as check) {
    <div class="pkf__check" [class.pkf__check--within]="check.outcome === true" [class.pkf__check--above]="check.outcome === false">
      <div class="pkf__check-result">
        <span class="pkf__eyebrow">{{ check.eyebrow }}</span>
        <h4><i class="fal" [ngClass]="check.outcome === true ? 'fa-circle-check' : check.outcome === false ? 'fa-circle-xmark' : 'fa-circle-question'" aria-hidden="true"></i>{{ check.result }}</h4>
        <p>{{ check.summary }}</p>
        @if (check.prior) {<p class="pkf__muted">{{ check.prior }}</p>}
        @if (check.flag) {<p class="pkf__flag" [class.pkf__flag--notable]="check.flag.tone === 'notable'"><i class="fal" [ngClass]="check.flag.tone === 'notable' ? 'fa-lightbulb' : 'fa-triangle-exclamation'" aria-hidden="true"></i>{{ check.flag.text }}</p>}
      </div>
      <ul class="pkf__limits" role="list">
        @for (limit of check.limits; track limit.label) {
          <li>
            <button type="button" class="pkf__limit" [class.pkf__limit--over]="limit.within === false" [disabled]="!canOpenReport" aria-haspopup="dialog" (click)="open(limit.reportTab)">
              <span class="pkf__limit-head"><span>{{ limit.label }}</span><span class="pkf__limit-status">{{ limit.within === null ? 'Not filed' : limit.within ? 'Within' : 'Over' }}</span></span>
              <span class="pkf__limit-values"><strong>{{ limit.value }}</strong><span> limit {{ limit.limit }}</span></span>
              <span class="pkf__meter" aria-hidden="true"><span class="pkf__meter-fill" [style.width.%]="meterFill(limit.ratio)"></span><span class="pkf__meter-limit"></span></span>
              @if (canOpenReport) {<span class="pkf__sr">View in the financial report, {{ tabLabel(limit.reportTab) }}</span>}
            </button>
          </li>
        }
      </ul>
      <p class="pkf__basis">{{ check.basis }}</p>
    </div>
  }

  @if (strip.advisers?.length) {
    <div class="pkf__advisers">
      <h4 class="pkf__eyebrow">Advisers and banker</h4>
      <dl>
        @for (adviser of strip.advisers; track adviser.role) {
          <div class="pkf__adviser" [class.pkf__adviser--missing]="!adviser.name">
            <dt>{{ adviser.role }}</dt>
            <dd><strong>{{ adviser.name || 'Not named' }}</strong><small>{{ adviser.source }}@if (adviser.period) { · {{ adviser.period }}}</small></dd>
          </div>
        }
      </dl>
    </div>
  }
</section>
}

<ng-template #figureBody let-figure>
  <span class="pkf__label">{{ figure.label }}</span>
  <span class="pkf__value-row">
    <span class="pkf__value"><strong>{{ figure.value }}</strong>@if (figure.unit) {<span class="pkf__unit">{{ figure.unit }}</span>}</span>
    @if (sparks[figure.id]; as spark) {
      <svg class="pkf__spark" [class.pkf__spark--bars]="spark.kind === 'bars'" viewBox="0 0 96 28" width="96" height="28" aria-hidden="true" focusable="false">
        @if (spark.zero !== null) {<line class="pkf__spark-zero" x1="0" x2="96" [attr.y1]="spark.zero" [attr.y2]="spark.zero"></line>}
        @for (bar of spark.bars; track $index) {<rect [attr.x]="bar.x" [attr.y]="bar.y" [attr.width]="bar.width" [attr.height]="bar.height" rx="1.5" [class.pkf__spark-latest]="bar.latest" [class.pkf__spark-negative]="bar.negative"></rect>}
        @for (segment of spark.segments; track $index) {<path [attr.d]="segment"></path>}
        @for (dot of spark.dots; track $index) {<circle [attr.cx]="dot.x" [attr.cy]="dot.y" r="2.6"></circle>}
      </svg>
      <span class="pkf__sr">{{ spark.text }}</span>
    }
  </span>
  @if (figure.periodLabel) {<span class="pkf__own-period">{{ figure.periodLabel }}</span>}
  @if (figure.detail) {<span class="pkf__detail">{{ figure.detail }}</span>}
  @if (figure.change; as change) {<span class="pkf__change" [class.pkf__change--down]="change.direction === 'down'"><i class="fal" [ngClass]="changeIcon(change.direction)" aria-hidden="true"></i><span aria-hidden="true">{{ change.amount || (change.direction === 'flat' ? 'No change' : change.direction === 'up' ? 'Up' : 'Down') }} vs prior period</span><span class="pkf__sr">{{ change.text }}</span></span>}
  @if (figure.flag) {<span class="pkf__flag" [class.pkf__flag--notable]="figure.flag.tone === 'notable'"><i class="fal" [ngClass]="figure.flag.tone === 'notable' ? 'fa-lightbulb' : 'fa-triangle-exclamation'" aria-hidden="true"></i>{{ figure.flag.text }}</span>}
  <span class="pkf__why">{{ figure.why }}</span>
</ng-template>
